import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbExternalLink } from "solid-icons/tb";
import { createMemo, For, Match, Show, Switch } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { SlackIcon } from "./slackIcon";
import { parseSlackSetupRequiredPayload, type SlackSetupRequiredPayload } from "./slackSetupRequiredSchema";

interface SlackSetupRequiredRendererProps {
  block: StreamingBlock;
}

const REASON_BODY_KEYS: Record<SlackSetupRequiredPayload["reason"], string> = {
  workspace_not_configured: "slack_setup_card.body_workspace_not_configured",
  not_exposed: "slack_setup_card.body_not_exposed",
  scope_not_authorized: "slack_setup_card.body_scope_not_authorized",
};

// The backend swaps this in for a Slack result when the org hasn't connected a
// Slack workspace (or hasn't enabled it for this project).
export const SlackSetupRequiredRenderer = (props: SlackSetupRequiredRendererProps) => {
  const rt = useChatRuntime();

  const isOrgAdmin = () => rt.ui?.isOrgAdmin?.() ?? false;
  const settingsHref = () => rt.actions.integrationSettingsHref?.("slack");

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<SlackSetupRequiredPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseSlackSetupRequiredPayload(json);
  });

  return (
    <Show
      when={payload()}
      fallback={
        <Show when={rawContent()}>
          <div class="rounded-lg border border-primary-border bg-panel p-4">
            <p class="text-xs text-muted-text">{rt.t("slack_setup_card.parse_error")}</p>
          </div>
        </Show>
      }
    >
      {(data) => (
        <div class="rounded-lg border border-primary-border bg-panel p-4">
          <div class="flex items-start gap-3">
            <SlackIcon class="size-6 shrink-0" />
            <div class="min-w-0 flex-1">
              <h3 class="text-sm font-medium text-primary-text">{rt.t("slack_setup_card.title")}</h3>
              <p class="mt-1 text-xs text-muted-text">{rt.t(REASON_BODY_KEYS[data().reason])}</p>
              <Show when={data().missingScopes?.length}>
                <div class="mt-2 flex flex-wrap items-center gap-1">
                  <span class="text-xs text-muted-text">{rt.t("slack_setup_card.missing_scopes_label")}</span>
                  <For each={data().missingScopes}>
                    {(scope) => (
                      <code class="rounded bg-inset px-1 font-mono text-[0.7rem] text-primary-text">{scope}</code>
                    )}
                  </For>
                </div>
              </Show>
            </div>
          </div>
          <Switch fallback={<p class="mt-3 text-xs text-muted-text">{rt.t("slack_setup_card.non_admin_note")}</p>}>
            <Match when={isOrgAdmin() && settingsHref()}>
              <a
                href={settingsHref()}
                class="mt-3 inline-flex items-center justify-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-on-brand transition-colors hover:bg-brand-hover"
              >
                <span>{rt.t("slack_setup_card.admin_cta")}</span>
                <TbExternalLink class="size-4" />
              </a>
            </Match>
            {/* Hosts that open settings out-of-process (chief → system browser)
                surface the same CTA as a button over the connectProvider seam. */}
            <Match when={rt.actions.connectProvider}>
              <button
                type="button"
                onClick={() => {
                  rt.actions.trackFeature?.({ feature: "chat.connect_provider", action: "slack", via: "button" });
                  rt.actions.connectProvider?.("slack");
                }}
                class="mt-3 inline-flex items-center justify-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-on-brand transition-colors hover:bg-brand-hover"
              >
                <span>{rt.t("slack_setup_card.admin_cta")}</span>
                <TbExternalLink class="size-4" />
              </button>
            </Match>
          </Switch>
        </div>
      )}
    </Show>
  );
};
